<template>
  <div class="block group">
    <section
      v-if="cover"
      class="block portal client-portal claim claim--open">
      <div class="portal__wrapper">
        <div class="portal__header">
          <a class="link" @click="back">
            <span>{{ getUiString(['general', 'my_claims']) }}</span>
            <lennoc-icon image="chevron-left" />
          </a>
          <h1 class="portal__title title--l">{{ getUiString(['claim_details', 'details', 'my_claim', 'claimnumber']) }} : Loading claim...</h1>
          <div class="flight-info flight-info__row">
            <div class="flight-info__section flight-info__section--dates">
              <lennoc-icon image="calendar" />
              <p class="date">Loading date...</p>
              <p class="flightnumber">Loading flightnumber...</p>
            </div>
            <div class="flight-info__section flight-indo__section--departure">
              <lennoc-icon image="airplane-take-off" />
              <p class="airport-name">Loading departure...</p>
              <p class="time">Loading time...</p>
            </div>
            <div class="flight-info__section flight-info__section--arrival">
              <lennoc-icon image="airplane-landing" />
              <p class="airport-name">Loading arrival...</p>
              <p class="time">Loading time...</p>
            </div>
          </div>
          <div class="tabs" id="nav">
            <div class="tabs__tab">
              <span class="tabs__text">
                <span>{{ getUiString(['claim_details', 'tabs', 'status']) }}</span>
                <span class="tabs__pill" v-if="warnings">{{ warnings }}</span>
              </span>
            </div>
            <div class="tabs__tab">
              <span class="tabs__text">
                <span>{{ getUiString(['claim_details', 'tabs', 'details']) }}</span>
              </span>
            </div>
            <div class="tabs__tab">
              <span class="tabs__text">
                <span>{{ getUiString(['claim_details', 'tabs', 'message']) }}</span>
              </span>
            </div>
          </div>
        </div>
        <lennoc-spinner v-if="busy" domclass="spinner__block-view"></lennoc-spinner>
      </div>
    </section>
    <section
      v-else
      class="portal client-portal claim claim--open">
      <div class="portal__wrapper">
        <div class="portal__header">
          <a class="link" @click="back">
            <span>{{ getUiString(['general', 'my_claims']) }}</span>
            <lennoc-icon image="chevron-left" />
          </a>
          <h1 class="portal__title title--l">{{ getUiString(['claim_details', 'details', 'my_claim', 'claimnumber']) }}: {{ currentClaim.ReferenceNumber }}</h1>
          <div class="group">
            <div v-for="relatedClaim in relatedClaims" :key="relatedClaim.Id" class="claim__type">
              <div class="text">{{ relatedClaimText(relatedClaim.ClaimType) }}</div>
              <a class="container-link" @click.prevent="linkToRelatedClaim(relatedClaim.Id)"></a>
              <div class="claim__type--box">
                <lennoc-icon :image="linkedClaimTypeIcon(relatedClaim.ClaimType)" :domClass="linkedClaimTypeIcon(relatedClaim.ClaimType)" />
              </div>
            </div>
          </div>
          <div class="flight-info flight-info__row">
            <div class="flight-info__section flight-info__section--dates">
              <lennoc-icon image="calendar" />
              <p class="date">{{ claimDepartureDate }}</p>
              <p class="flightnumber">{{ claimFlightCode }}</p>
            </div>
            <div class="flight-info__section flight-indo__section--departure">
              <lennoc-icon image="airplane-take-off" />
              <p class="airport-name">{{ claimDepartureAirport }}</p>
              <p class="time">{{ claimDepartureFlightDateTime }}</p>
            </div>
            <div class="flight-info__section flight-info__section--arrival">
              <lennoc-icon image="airplane-landing" />
              <p class="airport-name">{{ claimArrivalAirport }}</p>
              <p class="time">{{ claimArrivalFlightDateTime }}</p>
            </div>
          </div>
          <div class="tabs" id="nav">
            <div class="tabs__tab" :class="{ 'tabs__tab--active': activeTab === 'status' }">
              <span class="tabs__text">
                <span>{{ getUiString(['claim_details', 'tabs', 'status']) }}</span>
                <a class="container-link" @click.prevent="navigateTo('status')"></a>
                <span class="tabs__pill" v-if="warnings">{{ warnings }}</span>
              </span>
            </div>
            <div class="tabs__tab" :class="{ 'tabs__tab--active': activeTab === 'details' }">
              <span class="tabs__text">
                <span>{{ getUiString(['claim_details', 'tabs', 'details']) }}</span>
                <a class="container-link" @click.prevent="navigateTo('details')"></a>
              </span>
            </div>
            <div v-if="!currentClaim.ClaimClosed" class="tabs__tab" :class="{ 'tabs__tab--active': activeTab === 'bericht' }">
              <span class="tabs__text">
                <span>{{ getUiString(['claim_details', 'tabs', 'message']) }}</span>
                <a class="container-link" @click.prevent="navigateTo('bericht')"></a>
              </span>
            </div>
          </div>
        </div>
        <lennoc-spinner v-if="busy" domclass="spinner__block-view"></lennoc-spinner>
        <router-view />
      </div>
    </section>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { formatDate } from '@/utils/datetime'

export default {
  name: 'ClaimView',
  data () {
    return {
      activeTab: 'status',
      busy: true,
      cover: true
    }
  },
  mounted () {
    this.initClaim()
  },
  computed: {
    ...mapGetters({
      warnings: 'claim/getTaskCount',
      getUiString: 'uistrings/getUiString',
      currentClaim: 'claim/currentClaim',
      childClaims: 'dossier/getChildClaims',
      claimById: 'dossier/getClaimById'
    }),
    relatedClaims () {
      if (this.currentClaim.ParentClaimId) {
        const parentClaim = this.claimById(this.currentClaim.ParentClaimId)
        return parentClaim ? [parentClaim] : []
      }
      return this.childClaims(this.currentClaim.Id)
    },
    claimDepartureDate () {
      return this.currentClaim.DepartureFlightDateTime
        ? formatDate(this.currentClaim.DepartureFlightDateTime, 'date')
        : this.getUiString(['claim_details', 'details', 'my_claim', 'no_flight_date'])
    },
    claimFlightCode () {
      return this.currentClaim.AirlineCode && this.currentClaim.FlightNumber
        ? `${this.currentClaim.AirlineCode} ${this.currentClaim.FlightNumber}`
        : this.getUiString(['claim_details', 'details', 'my_claim', 'no_flight_code'])
    },
    claimDepartureAirport () {
      return this.currentClaim.DepartureAirport === 'Unknown'
        ? this.getUiString(['claim_details', 'details', 'my_claim', 'no_departure_airport'])
        : this.currentClaim.DepartureAirport
    },
    claimArrivalAirport () {
      return this.currentClaim.ArrivalAirport === 'Unknown'
        ? this.getUiString(['claim_details', 'details', 'my_claim', 'no_arrival_airport'])
        : this.currentClaim.ArrivalAirport
    },
    flightDateFormat () {
      return this.currentClaim.ClaimType === 'Voucher_Flight' ? 'date' : 'datetime'
    },
    claimDepartureFlightDateTime () {
      return this.currentClaim.DepartureFlightDateTime
        ? formatDate(this.currentClaim.DepartureFlightDateTime, this.flightDateFormat)
        : this.getUiString(['claim_details', 'details', 'my_claim', 'no_departure_time'])
    },
    claimArrivalFlightDateTime () {
      return this.currentClaim.ArrivalFlightDateTime
        ? formatDate(this.currentClaim.ArrivalFlightDateTime, this.flightDateFormat)
        : this.getUiString(['claim_details', 'details', 'my_claim', 'no_arrival_time'])
    }
  },
  methods: {
    async initClaim () {
      try {
        await this.$store.dispatch('claim/initializeClaim', this.$route.params.claimid)
      } catch (error) {
        this.error = true
      }
      this.cover = false
      this.busy = false
    },
    navigateTo (route) {
      this.$router.push(`/claim/${this.currentClaim.Id}/${route}`)
    },
    back () {
      this.busy = true
      this.$store.dispatch('refreshDossier').then(
        () => {
          this.$router.push('/home/claims').then(
            () => {
              this.$store.dispatch('claim/clearClaim')
            }
          )
        }
      )
    },
    formatDate (dateTime, format) {
      return formatDate(dateTime, format)
    },
    linkedClaimTypeIcon (claimType) {
      switch (claimType) {
        case 'Regulation_261_2004':
          return 'plane-flat'
        case 'Regulation_889_2002':
          return 'baggage'
        case 'Regulation_889_2002_Flight':
          return 'extra-costs'
        default:
          return 'plane-flat'
      }
    },
    linkToRelatedClaim (claimId) {
      this.busy = true
      this.error = false
      this.$router.push(`/claim/${claimId}/status`)
      this.initClaim()
    },
    relatedClaimText (claimType) {
      switch (claimType) {
        case 'Regulation_261_2004':
          return this.getUiString(['claim_details', 'claim', 'view_flight_claim'])
        case 'Regulation_889_2002':
          return this.getUiString(['claim_details', 'claim', 'view_luggage_claim'])
        case 'Regulation_889_2002_Flight':
          return this.getUiString(['claim_details', 'claim', 'view_costs_claim'])
      }
    }
  },
  watch: {
    $route (to, from) {
      this.activeTab = to.name
    }
  }
}
</script>
